export type TransitionPresetName = 'micro' | 'quick' | 'standard' | 'expressive' | 'dramatic'

export type TransitionContext =
  | 'system'
  | 'window'
  | 'overlay'
  | 'navigation'
  | 'taskbar'
  | 'popover'

export type MotionPerformanceTier = 'low' | 'medium' | 'high'

export interface MotionSettings {
  scale: number
  performance: MotionPerformanceTier
  prefersReduced: boolean
}

export interface TransitionOverrides {
  context?: TransitionContext
  phase?: 'enter' | 'exit'
  multiplier?: number
  easing?: string
  delay?: number
  clamp?: {
    min?: number
    max?: number
  }
}

export interface TransitionTiming {
  duration: number
  easing: string
  delay: number
}

const TRANSITION_PRESETS: Record<TransitionPresetName, { duration: number; easing: string }> = {
  micro: { duration: 120, easing: 'var(--motion-ease-soft)' },
  quick: { duration: 180, easing: 'var(--motion-ease-standard)' },
  standard: { duration: 260, easing: 'var(--motion-ease-standard)' },
  expressive: { duration: 340, easing: 'var(--motion-ease-emphasized)' },
  dramatic: { duration: 480, easing: 'var(--motion-ease-emphasized)' },
}

const CONTEXT_MULTIPLIERS: Record<TransitionContext, number> = {
  system: 1,
  window: 1.08,
  overlay: 0.92,
  navigation: 0.9,
  taskbar: 0.85,
  popover: 0.88,
}

const PHASE_MULTIPLIERS: Record<'enter' | 'exit', number> = {
  enter: 0.95,
  exit: 1.05,
}

const PERFORMANCE_MULTIPLIERS: Record<MotionPerformanceTier, number> = {
  low: 0.88,
  medium: 1,
  high: 1.08,
}

const MIN_DURATION = 70

const MAX_DURATION = 650

export function resolveTransitionTiming(
  preset: TransitionPresetName,
  settings: MotionSettings,
  overrides: TransitionOverrides = {},
): TransitionTiming {
  const { duration, easing } = TRANSITION_PRESETS[preset] ?? TRANSITION_PRESETS.standard
  const context = overrides.context ?? 'system'
  const phase = overrides.phase ?? 'enter'

  const performanceScale = PERFORMANCE_MULTIPLIERS[settings.performance]
  const effectiveScale = settings.prefersReduced ? Math.min(settings.scale, 0.3) : settings.scale

  let computedDuration =
    duration *
    effectiveScale *
    performanceScale *
    (CONTEXT_MULTIPLIERS[context] ?? 1) *
    PHASE_MULTIPLIERS[phase]

  if (overrides.multiplier !== undefined) {
    computedDuration *= overrides.multiplier
  }

  const clampMin = overrides.clamp?.min ?? MIN_DURATION
  const clampMax = overrides.clamp?.max ?? MAX_DURATION

  const finalDuration = Math.max(clampMin, Math.min(clampMax, Math.round(computedDuration)))

  return {
    duration: finalDuration,
    easing: overrides.easing ?? easing,
    delay: overrides.delay ?? 0,
  }
}
